/**
 * Brokerage settings — Schwab connection, SIM/LIVE mode, kill switch.
 */
import { useEffect, useState } from 'react';
import * as api from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../lib/toast';

export function BrokerageSettings() {
  return (
    <div className="space-y-4">
      <ModeSection />
      <SchwabSection />
      <ResetSimSection />
    </div>
  );
}

function ModeSection() {
  const { me, refresh } = useAuth();
  const toast = useToast();
  const [showFlip, setShowFlip] = useState(false);
  const [busy, setBusy] = useState(false);

  async function killSwitch() {
    setBusy(true);
    try {
      await api.settings.killSwitch();
      await refresh();
      toast.show('Switched to SIM mode', 'ok');
    } catch (e: unknown) {
      toast.show((e as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section title="Trading mode">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            current:
            <span className={me?.mode === 'live' ? 'pill-live' : 'pill-sim'}>
              {(me?.mode ?? 'sim').toUpperCase()}
            </span>
          </div>
          <div className="text-[11px] text-ink-mute leading-snug">
            <span className="block">
              SIM trades a <strong className="text-ink">paper book</strong>{' '}
              against your <strong className="text-ink">real account state</strong>{' '}
              (shadow trading; reads from Schwab when connected).
            </span>
            <span className="block">
              LIVE places <strong className="text-bad">real orders</strong>{' '}
              against your Schwab account.
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <button className="btn-danger text-xs" onClick={killSwitch} disabled={busy || me?.mode === 'sim'} title="instantly flip back to SIM">
            kill switch
          </button>
          <button className="btn" onClick={() => setShowFlip(true)} disabled={busy}>
            switch to {me?.mode === 'live' ? 'SIM' : 'LIVE'}
          </button>
        </div>
      </div>
      {showFlip && (
        <ModeFlipModal
          target={me?.mode === 'live' ? 'sim' : 'live'}
          onClose={() => setShowFlip(false)}
          onResult={async () => { setShowFlip(false); await refresh(); }}
        />
      )}
    </Section>
  );
}

function ModeFlipModal({ target, onClose, onResult }: { target: 'sim' | 'live'; onClose: () => void; onResult: () => Promise<void> }) {
  const toast = useToast();
  const [pw, setPw] = useState('');
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const isLive = target === 'live';

  async function go() {
    setBusy(true);
    try {
      await api.settings.changeMode(target, pw, isLive && ack);
      toast.show(`Switched to ${target.toUpperCase()}`, isLive ? 'warn' : 'ok');
      await onResult();
    } catch (e: unknown) {
      toast.show((e as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="card p-5 max-w-md w-full mx-4">
        <div className={`font-semibold mb-2 ${isLive ? 'text-bad' : 'text-ok'}`}>
          {isLive
            ? 'Switch to LIVE (real money)'
            : 'Switch to SIM (paper book over real account state)'}
        </div>
        <div className="text-xs text-ink-mute mb-3">Your password is required for any mode change.</div>
        <input className="input w-full mb-3" type="password" placeholder="password" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus />
        {isLive && (
          <label className="flex items-start gap-2 text-xs text-ink-mute mb-3">
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
            <span>I acknowledge that LIVE mode places real orders that may result in real loss.</span>
          </label>
        )}
        <div className="flex justify-end gap-2">
          <button className="btn" onClick={onClose} disabled={busy}>cancel</button>
          <button className={isLive ? 'btn-danger' : 'btn-primary'} onClick={go} disabled={!pw || (isLive && !ack) || busy}>
            {busy ? '...' : isLive ? 'flip to LIVE' : 'flip to SIM'}
          </button>
        </div>
      </div>
    </div>
  );
}

function SchwabSection() {
  const toast = useToast();
  const [status, setStatus] = useState<api.SchwabStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [appKey, setAppKey] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [testOut, setTestOut] = useState<unknown | null>(null);

  useEffect(() => {
    api.schwab.status().then(setStatus).catch((e) => toast.show(e.message, 'bad'));
  }, []);

  async function saveCreds() {
    setBusy(true);
    try {
      await api.schwab.setCredentials(appKey, appSecret);
      setAppKey('');
      setAppSecret('');
      setStatus(await api.schwab.status());
      toast.show('Credentials saved', 'ok');
    } catch (e: unknown) {
      toast.show((e as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  async function startOAuth() {
    try {
      const { url } = await api.schwab.authorizeUrl();
      window.open(url, '_blank', 'noopener');
    } catch (e: unknown) {
      toast.show((e as Error).message, 'bad');
    }
  }

  async function testConn() {
    setBusy(true);
    try {
      setTestOut(await api.schwab.testConnection());
      toast.show('Connection OK', 'ok');
    } catch (e: unknown) {
      toast.show((e as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  async function disconnect() {
    setBusy(true);
    try {
      await api.schwab.disconnect();
      setTestOut(null);
      setStatus(await api.schwab.status());
      toast.show('Disconnected', 'ok');
    } finally {
      setBusy(false);
    }
  }

  async function syncNow() {
    setBusy(true);
    try {
      const r = await api.schwab.sync();
      setTestOut(r);
      if (r.ok) {
        const created = (r.positions?.cost_basis_rows_created as number) ?? 0;
        const refined = r.lots?.refined?.length ?? 0;
        toast.show(
          `Sync complete: ${created} new lot(s), ${refined} symbol(s) refined from ${r.transactions_parsed} transactions`,
          'ok',
        );
      } else {
        toast.show(r.detail ?? 'Sync unavailable', 'warn');
      }
    } catch (e: unknown) {
      toast.show((e as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  if (!status) return <Section title="Schwab brokerage">Loading...</Section>;

  return (
    <Section title="Schwab brokerage" subtitle="Connect your Schwab account to enable LIVE mode.">
      <div className="space-y-3 text-xs">
        <div className="flex items-center gap-2">
          <span className={status.connected ? 'pill-sim' : 'pill text-warn border-[#5c4720]'}>
            {status.connected ? 'connected' : status.has_credentials ? 'creds saved' : 'not configured'}
          </span>
          {status.account_id && <span className="text-ink-mute num">acct: {status.account_id}</span>}
        </div>
        {!status.has_credentials && (
          <div className="space-y-2">
            <label className="block">
              <span className="text-ink-mute">App Key (Client ID)</span>
              <input className="input w-full mt-1 font-mono" value={appKey} onChange={(e) => setAppKey(e.target.value)} placeholder="e.g. AbCdEf..." />
            </label>
            <label className="block">
              <span className="text-ink-mute">App Secret</span>
              <input className="input w-full mt-1 font-mono" type="password" value={appSecret} onChange={(e) => setAppSecret(e.target.value)} placeholder="paste secret" />
            </label>
            <button className="btn" onClick={saveCreds} disabled={busy || !appKey || !appSecret}>save credentials</button>
          </div>
        )}
        {status.has_credentials && !status.connected && (
          <div>
            <button className="btn-primary" onClick={startOAuth} disabled={busy}>connect via OAuth</button>
            <span className="ml-2 text-ink-mute">opens Schwab consent in a new tab.</span>
          </div>
        )}
        {status.connected && (
          <div className="flex gap-2">
            <button className="btn" onClick={testConn} disabled={busy}>test connection</button>
            <button className="btn" onClick={syncNow} disabled={busy} data-testid="schwab-sync-btn" title="Import positions + refine per-lot cost basis from transaction history">
              {busy ? 'syncing...' : 'sync holdings'}
            </button>
            <button className="btn" onClick={disconnect} disabled={busy}>disconnect</button>
          </div>
        )}
        {testOut !== null && testOut !== undefined && (
          <pre className="text-[11px] num text-ink-mute overflow-x-auto bg-bg-elev p-2 rounded">
            {JSON.stringify(testOut, null, 2)}
          </pre>
        )}
      </div>
    </Section>
  );
}

function ResetSimSection() {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState<{ sim_orders_deleted: number; wizard_drafts_deleted: number } | null>(null);

  async function doReset() {
    setBusy(true);
    try {
      const r = await api.settings.resetSim();
      setResult(r);
      setConfirm(false);
      toast.show(`Reset complete: ${r.sim_orders_deleted} orders + ${r.wizard_drafts_deleted} drafts deleted`, 'ok');
    } catch (e: unknown) {
      toast.show((e as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section title="Reset simulation data" subtitle="Delete all SIM trades, positions, orders, and wizard drafts. LIVE data and thesis research are always preserved.">
      <div className="space-y-3 text-xs">
        {result && (
          <div className="text-ok text-xs">
            Last reset: {result.sim_orders_deleted} SIM orders + {result.wizard_drafts_deleted} drafts deleted; SIM ledger cleared and cash re-seeded. LIVE data preserved.
          </div>
        )}
        {!confirm ? (
          <button className="btn text-xs" onClick={() => setConfirm(true)} data-testid="reset-sim-btn">
            Reset SIM data
          </button>
        ) : (
          <div className="card p-3 border-warn space-y-2" data-testid="reset-sim-confirm">
            <div className="text-warn font-semibold text-xs">Are you sure?</div>
            <div className="text-ink-mute text-[11px]">
              This will permanently delete all simulated trades, persisted SIM positions, pending SIM orders, and wizard drafts, and re-seed your SIM cash to $100k.
              Your thesis research, LIVE orders, and cost basis are never touched.
            </div>
            <div className="flex gap-2">
              <button className="btn-danger text-xs" onClick={doReset} disabled={busy} data-testid="reset-sim-confirm-yes">
                {busy ? 'Resetting...' : 'Yes, reset SIM data'}
              </button>
              <button className="btn text-xs" onClick={() => setConfirm(false)} disabled={busy}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </Section>
  );
}

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="card p-4">
      <div className="text-sm font-semibold mb-1">{title}</div>
      {subtitle && <div className="text-[11px] text-ink-mute mb-3">{subtitle}</div>}
      {children}
    </div>
  );
}
